import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { invalidFaxDetailsApi } from '../../../../../lib/fetchApi'
import { notificationActions } from '../../../../../store/notifications'
import { workqueueActions } from '../../../../../store/workqueue'
import MockLoadingBlock from '../../../../mock/MockLoadingBlock'
import Button from '../../../../common/Button'
import InvalidFaxEditModal from './InvalidFaxEditModal'
import classes from './InvalidFaxWorkQ.module.css'
import layoutClasses from '../../WorkQComponent.module.css'

const GROUPS = [
    { key: 'locations', title: 'Locations' },
    { key: 'attorneys', title: 'Attorneys', hideWithoutInvalid: true },
    { key: 'case_managers', title: 'Case managers', hideWithoutInvalid: true },
]

export default function InvalidFaxDetails() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { patientId } = useParams()
    const userToken = useSelector(state => state.user.token)
    const patientsList = useSelector(state => state.filter.currentpagedata)
    const [loading, setLoading] = useState(true)
    const [detail, setDetail] = useState(null)
    const [editing, setEditing] = useState(null)

    useEffect(() => {
        if (!userToken || !patientId || patientId === 'undefined' || patientId === 'null') {
            return
        }

        let cancelled = false

        const load = async () => {
            setLoading(true)
            setDetail(null)
            const result = await invalidFaxDetailsApi(userToken, patientId)
            if (cancelled) {
                return
            }

            if (!result || result.errorMessage || result.status) {
                const message = typeof result?.errorMessage === 'string'
                    ? result.errorMessage
                    : 'This law firm could not be opened.'
                dispatch(notificationActions.setError({ errorMessage: message }))
                setDetail(null)
            } else {
                setDetail(result)
            }
            setLoading(false)
        }

        load()

        return () => {
            cancelled = true
        }
    }, [patientId, userToken, dispatch])

    function savedHandler(saved) {
        setEditing(null)
        if (!detail) {
            return
        }

        const next = {
            ...detail,
            locations: (detail.locations || []).filter(row => !sameRow(row, saved)),
            attorneys: (detail.attorneys || []).filter(row => !sameRow(row, saved)),
            case_managers: (detail.case_managers || []).filter(row => !sameRow(row, saved)),
        }
        setDetail(next)
        dispatch(workqueueActions.setRefreshApi())
    }

    useEffect(() => {
        if (!detail || !patientId) {
            return
        }
        const remaining = (detail.locations?.length || 0) + (detail.attorneys?.length || 0) + (detail.case_managers?.length || 0)
        if (remaining > 0) {
            return
        }
        const listed = Array.isArray(patientsList?.data) && patientsList.data.some(item => String(item.id) === String(patientId))
        const listIsThisQueue = patientsList?.links?.first?.includes('work-queues-invalid-fax')
        if (listIsThisQueue && !listed) {
            navigate('/work-queues/invalid-fax-q', { replace: true })
        }
    }, [detail, patientId, patientsList, navigate])

    if (loading) {
        return (
            <div className={`${layoutClasses.police_report_side} ${classes.panel}`}>
                <MockLoadingBlock />
            </div>
        )
    }

    if (!detail) {
        return (
            <div className={`${layoutClasses.police_report_side} ${classes.panel}`}>
                <p>This law firm could not be opened.</p>
            </div>
        )
    }

    return (
        <div className={`${layoutClasses.police_report_side} ${classes.panel}`}>
            <h2 className={classes.firm_name}>{detail.law_firm?.name}</h2>
            {GROUPS.map(group => {
                const rows = rowsForGroup(detail[group.key], group.hideWithoutInvalid)
                if (rows.length === 0) {
                    return null
                }
                return (
                    <section key={group.key} className={classes.group}>
                        <div className={classes.group_header}>
                            <h3>{group.title}</h3>
                            <span className={classes.group_count}>{rows.length}</span>
                        </div>
                        <table className={classes.table}>
                            <thead>
                                <tr>
                                    <th className={classes.col_name}>Name</th>
                                    <th className={classes.col_fax}>Fax</th>
                                    <th className={classes.issue_cell}>Issue</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(row => (
                                    <tr key={`${row.type}-${row.id}`}>
                                        <td className={classes.row_name}>{row.name}</td>
                                        <td className={row.fax ? undefined : classes.muted}>{row.fax || 'No fax number'}</td>
                                        <td className={`${classes.issue_cell} ${row.invalid_fax_error ? classes.row_error : classes.muted}`}>
                                            {row.invalid_fax_error || '—'}
                                        </td>
                                        <td className={classes.edit_cell}>
                                            <Button type="button" btnStyle="btn_white" title="Edit fax" click={() => setEditing(row)}>
                                                <i className="ti ti-pencil"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                )
            })}
            {editing && (
                <InvalidFaxEditModal
                    item={editing}
                    onClose={() => setEditing(null)}
                    onSaved={savedHandler}
                />
            )}
        </div>
    )
}

function rowsForGroup(rows, onlyInvalid) {
    const list = rows || []
    if (!onlyInvalid) {
        return list
    }
    return list.filter(row => row.invalid_fax_error)
}

function sameRow(row, saved) {
    return row.type === saved.type && String(row.id) === String(saved.id)
}
